import React, { useEffect, useRef, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TablePagination, Box, Checkbox, TextField, MenuItem,
  FormControlLabel,
  FormGroup,
  Grid,
  alpha
} from '@mui/material';
import { EApprovedFromAssociation, UserInfosModel } from '../../../../models/user/UserInfosModel';
import { useNavigate } from 'react-router-dom'; // Per la navigazione
import { useTranslationContext } from '../../../../hooks/translations/translationsProvider';
import { BLACK, BLUE_LIGHT_RGB_BORDER, GREEN_LOGO, GREEN_MAIN, WHITE } from '../../../../theme/colors';
import { usersPrivateList } from '../../../../hooks/actions/admin';
import { delay } from '../../../../utilities/delay';
import CustomWaiter from '../../../Waiter';
import Search from '../../../Search';
import { theme } from '../../../../theme';
import { CustomTableContainer } from '../../style';

interface Props {
  onUserSelect: (user: UserInfosModel) => void;
}

const PrivateUsersTable: React.FC<Props> = ({
  onUserSelect,
}) => {
  const [users, setUsers] = useState<UserInfosModel[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [enabledFilter, setEnabledFilter] = useState<boolean | null>(null);
  const [approvalFilter, setApprovalFilter] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [searchWaiter, setSearchWaiter] = useState<boolean>(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const {t} = useTranslationContext();
  const minChars = 2;

  const fetchUsers = () => {
    usersPrivateList(page, rowsPerPage, searchTerm, approvalFilter, enabledFilter)
      .then(response => {
        
        setUsers(response.users);
        setTotalRecords(response.totalRecords);
      })
      .catch(e => console.error(e));

    delay(500).then(()=>{
      setSearchWaiter(false);
    })
  };

  useEffect(() => {
    fetchUsers();
  },[]); // ricerca all'avvio

   useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    setSearchWaiter(true);
    debounceTimeout.current = setTimeout(() => {
      fetchUsers();
      debounceTimeout.current = null;
    }, 1200);
  }, 
  [page, rowsPerPage, enabledFilter, approvalFilter, searchTerm]);

  const handleInput = (newString: string) => {
    if (newString.length < minChars) {
      setSearchTerm(null);
      return;
    }
    if (newString === searchTerm) return;
    if (newString.length > 20) return;
    setSearchTerm(newString);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (value: boolean | null) => {
    setEnabledFilter((prev) => (prev === value ? null : value));
  };


  return (
    <Paper sx={{ 
      width: '100%', 
      overflow: 'hidden', 
      borderColor: 'transparent',
      maxWidth: '100%',
      boxSizing: 'border-box',
      boxShadow: 'none'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems:'center', padding: 4 }}>
        <Grid container>
          <Grid item xs={12} sm={6} display={'flex'}>
            <Search 
                handleInput={handleInput}
                search={searchTerm!!}
                setSearch={setSearchTerm}
            />
            
            <div style={{
                paddingTop:'3px',
                paddingLeft:'3px',
                width:'50px',
                height:'35px'
            }}>
                {searchWaiter && <CustomWaiter size={30} width={40}/>}
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t("Operatività")}
                value={approvalFilter ?? ''} 
                onChange={(e) => setApprovalFilter(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                variant="outlined"
                sx={{ 
                  width: '100%',
                  height:'35px',
                  backgroundColor: `${GREEN_LOGO}10`,
                  '&:hover': {
                    backgroundColor: `${GREEN_LOGO}20`,
                  },
                  '& .MuiInputLabel-root': {
                    marginTop: '-6px',
                    color:GREEN_MAIN,
                    fontSize: '1rem'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius:'5px',
                    border:`1px solid ${GREEN_LOGO}20`
                  },
                  '& .MuiSelect-select': {
                    padding: '12px 12px',
                    lineHeight: 1.4,
                    color: "grey",
                    fontSize: '1rem',
                    textTransform: 'none'
                  },
                  '& .MuiMenuItem-root': {
                    fontSize: '1rem',
                    color: GREEN_MAIN,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 128, 0, 0.08)'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 128, 0, 0.12)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 128, 0, 0.16)'
                      }
                    }
                  }
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          fontSize: '1rem',
                          color: GREEN_MAIN,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 128, 0, 0.08)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 128, 0, 0.12)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 128, 0, 0.16)'
                            }
                          }
                        }
                      }
                    }
                  }
                }}
                InputProps={{
                  style: {
                    height: '35px',
                  },
                }}
              >
                <MenuItem value="">
                  <em>{t("Tutti gli status")}</em>
                </MenuItem>
                {Object.keys(EApprovedFromAssociation).map((key) => {
                  const typedKey = key as keyof typeof EApprovedFromAssociation;

                  if (isNaN(Number(typedKey))) {
                    const value = EApprovedFromAssociation[typedKey];
                    const displayValue = value === 0 ? 0 : value;
                    
                    return (
                      <MenuItem key={typedKey} value={displayValue}>
                        {typedKey === 'FirstRequest' ? t('FirstRequest') : t(typedKey)}
                      </MenuItem>
                    );
                  }
                  return null;
                })}
              </TextField>
            </Grid>
          <Grid item xs={12}mt={2}>
            <FormGroup row sx={{justifyContent:'center'}}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={enabledFilter === true}
                            onChange={() => handleFilterChange(true)}
                            color="primary"
                            sx={{
                              '&. MuiCheckbox-root': {
                              },
                            }}
                        />
                    }
                    label={
                        <span style={{ color: "black", fontSize: '0.8rem' }}>
                            {t("Utenti Abilitati")}
                        </span>
                    }
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={enabledFilter === false}
                            onChange={() => handleFilterChange(false)}
                            color="primary"
                            sx={{
                              '&.MuiCheckbox-root': {
                              },
                            }}
                        />
                    }
                    label={
                        <span style={{ color: "black", fontSize: '0.8rem' }}>
                            {t("Utenti Disabilitati")}
                        </span>
                    }
                />
            </FormGroup>
          </Grid>
        </Grid>
      </Box>

      <CustomTableContainer >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Nome")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Cognome")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Email")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Status")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Abilitato")}</TableCell>
              {/*<TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Codice Fiscale")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Data di Nascita")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Indirizzo")}</TableCell>*/}
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Città")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Nazione")}</TableCell>
              {/*<TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("CAP")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Provincia")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Nazione")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Referral Code")}</TableCell>
              <TableCell sx={{
                fontWeight:'bold', 
                whiteSpace:'nowrap',
                backgroundColor: 'transparent',
                color: GREEN_MAIN,
                border: '0px'
              }}>{t("Data Eliminazione dalla Associazione")}</TableCell>*/}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.userId} onClick={() => onUserSelect(user)} sx={{
                backgroundColor: user.enabledUser && user.approvedFromAssociation === EApprovedFromAssociation.ApprovedFromAssociation ? 'RGB(0,255,0,0.15)' : 
                                !user.enabledUser && user.approvedFromAssociation === EApprovedFromAssociation.FirstRequest ? WHITE :
                                !user.enabledUser || user.approvedFromAssociation === EApprovedFromAssociation.RemovedFromAssociation ? 'RGB(255,0,0,0.15)' : WHITE,
                cursor:'pointer'
              }}>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.nome}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.cognome}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.email}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{t(EApprovedFromAssociation[user.approvedFromAssociation ?? 0])}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.enabledUser ? t('Sì') : t('No')}</TableCell>
                {/*<TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.codiceFiscale}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.born ? new Date(user.privateInfo?.born).toLocaleDateString() : ''}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.address}</TableCell>*/}
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.city}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.nation}</TableCell>
                {/*<TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.cap}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.provincia}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.privateInfo?.nation}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>{user.refCode}</TableCell>
                <TableCell sx={{ borderBottom: '0px', padding: '8px 16px' }}>
                  {user.removedFromAssociation &&
                  new Date(user.removedFromAssociation).getFullYear() !== 1
                    ? new Date(user.removedFromAssociation).toLocaleDateString()
                    : '-'}
                </TableCell>*/}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CustomTableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        labelRowsPerPage={t("Risultati per pagina")}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("di")} ${count}`}
        sx={{
          color:GREEN_MAIN,
          "& .MuiTablePagination-select": {
              "&:focus": {
                backgroundColor: "transparent",
              },
          },
          // This targets the dropdown arrow icon
          "& .MuiTablePagination-selectIcon": {
              color: GREEN_MAIN,
          },
          // This targets the navigation arrows
          "& .MuiTablePagination-actions": {
              "& .MuiIconButton-root": {
                color: GREEN_MAIN,
              },
          },
          // Optional: Style the text to match
          "& .MuiTablePagination-displayedRows": {
              color: GREEN_MAIN,
          },
          "& .MuiTablePagination-selectLabel": {
              color: GREEN_MAIN,
          },
          // Add scrollbar styling
          "&::-webkit-scrollbar": {
            height: "8px",
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.main,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
          "&::-webkit-scrollbar-button": {
            display: "none !important",
            width: "0px",
            height: "0px",
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${GREEN_LOGO} ${theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d"}`,
          msOverflowStyle: "none",
          overflowX: "auto",
          maxWidth: "100%",
        }}
      />
    </Paper>
  );
};

export default PrivateUsersTable;
